import { useMemo, useRef, useEffect, useState, forwardRef } from "react";
import { useTranslation } from "react-i18next";
import HTMLFlipBook from "react-pageflip";
import SiddurPage from "./SiddurPage";

/**
 * SiddurBookReader — 3D page-flip book for the siddur, mirroring the
 * Tanakh `BookReader3D` so users get one consistent "physical book"
 * experience across the app.
 *
 * Why a separate component (not a generalised BookReader)
 * -------------------------------------------------------
 * The Tanakh reader is verse-centric: each datum has a `verse` index,
 * a chapter, and verse-number rendering. The siddur is paragraph-
 * centric, grouped by section/subsection, with no chapter or verse
 * numbers. Forcing them into one component would mean accepting both
 * shapes everywhere — high refactor surface, real risk of breaking
 * the production Tanakh flow. Instead, this component is a focused
 * sibling that:
 *
 *   • uses the SAME `react-pageflip` library
 *   • uses the SAME `.book-page-paper` / `.book-page-inner` CSS, so
 *     pages look visually identical to Tanakh pages
 *   • applies the SAME RTL reversal trick (feed pages reversed to a
 *     hard-wired LTR library; rely on `dir="rtl"` for text)
 *   • exposes the SAME flipForward / flipBackward / goTo imperative
 *     API via ref, so toolbars built for Tanakh can be reused
 *
 * Pagination
 * ----------
 * Sections always start on a fresh page (so the user never misses a
 * section title). Within a section, we pack subsections into pages
 * using a character budget tuned for the page font (~1100 chars per
 * page on desktop, ~700 on mobile). A subsection's title + its
 * paragraphs counts as one unit; if the unit is larger than a page,
 * the paragraphs split between pages but the subsection title stays
 * with the first chunk.
 *
 * Filtering (mon/thu, tachanun skip) is the caller's responsibility
 * — `visibleSections` arrives pre-filtered and pre-tagged, just like
 * the scrolling renderer.
 */
const SiddurBookReader = forwardRef(function SiddurBookReader(
  {
    sections = [],          // pre-filtered visible sections (same shape as backend)
    tStatus = null,         // for tachanun-skip banner inside book pages
    showInstructions = false, // book mode = false, study mode is rendered separately
    onPageFlip,             // (pageData) — called when reader flips
    titleHe = "סידור",
    titleEn = "SIDDUR",
  },
  ref,
) {
  const { t } = useTranslation();
  const bookRef = useRef(null);
  const stageRef = useRef(null);

  // Track container width so the book fits its actual stage rather
  // than the entire viewport. ResizeObserver-driven, matches Tanakh.
  const [stageWidth, setStageWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200,
  );
  const [dims, setDims] = useState(() =>
    computeDims(
      typeof window !== "undefined" ? window.innerWidth : 1200,
      typeof window !== "undefined" ? window.innerHeight : 900,
    ),
  );

  useEffect(() => {
    const el = stageRef.current;
    const recompute = () => {
      const w = el?.getBoundingClientRect?.()?.width || window.innerWidth;
      setStageWidth(Math.round(w));
      setDims(computeDims(w, window.innerHeight));
    };
    if (!el || typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", recompute);
      window.addEventListener("orientationchange", recompute);
      return () => {
        window.removeEventListener("resize", recompute);
        window.removeEventListener("orientationchange", recompute);
      };
    }
    const ro = new ResizeObserver((entries) => {
      const w = Math.round(entries[0]?.contentRect?.width || window.innerWidth);
      setStageWidth(w);
    });
    ro.observe(el);
    window.addEventListener("orientationchange", recompute);
    return () => {
      ro.disconnect();
      window.removeEventListener("orientationchange", recompute);
    };
  }, []);

  useEffect(() => {
    setDims(computeDims(stageWidth, window.innerHeight));
  }, [stageWidth]);

  // Build pages from the section/subsection tree. The result is in
  // NATURAL reading order (page 0 = first prayer, last page = last).
  // The Tanakh's per-screen character budget (~820 chars) is a good
  // starting point; the siddur uses slightly tighter pages because
  // paragraphs are typically longer than single verses and the
  // `<b>`-emphasised opening word + niqqud add visual weight.
  const pages = useMemo(() => {
    return buildSiddurPages({
      sections,
      tStatus,
      maxCharsPerPage: dims.portrait ? 700 : 1100,
    });
  }, [sections, tStatus, dims.portrait]);

  // Library is LTR — feed reversed order so swipes flip in the
  // direction of reading a real Hebrew sefer. See BookReader3D
  // file header for the full rationale.
  const renderedPages = useMemo(() => [...pages].reverse(), [pages]);
  const startPage = renderedPages.length - 1;

  // Imperative API. Naming mirrors human reading-intent: forward
  // means "next page of prayer", which under the RTL reversal calls
  // the library's flipPrev. We also expose .goTo(semanticIndex) so
  // the bookmark restore can jump straight to a saved page.
  useEffect(() => {
    if (!ref) return;
    const api = {
      flipForward: () => {
        const pf = bookRef.current?.pageFlip();
        if (!pf) return;
        try { pf.flipPrev(); } catch {}
      },
      flipBackward: () => {
        const pf = bookRef.current?.pageFlip();
        if (!pf) return;
        try { pf.flipNext(); } catch {}
      },
      currentPage: () => {
        const pf = bookRef.current?.pageFlip();
        if (!pf) return 0;
        try {
          const renderedIdx = pf.getCurrentPageIndex();
          return renderedPages.length - 1 - renderedIdx;
        } catch { return 0; }
      },
      pageCount: () => pages.length,
      goTo: (semanticIndex) => {
        if (typeof semanticIndex !== "number") return false;
        const renderedIdx = renderedPages.length - 1 - semanticIndex;
        // In portrait (single-page) mode the library flips one page at
        // a time, so don't snap to a spread — that snapping is what
        // makes section-tab taps land on the WRONG page on mobile.
        const target = dims.portrait
          ? renderedIdx
          : renderedIdx - (renderedIdx % 2);
        const pf = bookRef.current?.pageFlip();
        if (!pf || typeof pf.flip !== "function") return false;
        try { pf.flip(Math.max(0, target)); } catch { return false; }
        return true;
      },
      goToSection: (sectionId) => {
        if (!sectionId) return false;
        const idx = pages.findIndex((p) => p.sectionId === sectionId);
        if (idx < 0) return false;
        const renderedIdx = renderedPages.length - 1 - idx;
        const target = dims.portrait
          ? renderedIdx
          : renderedIdx - (renderedIdx % 2);
        const pf = bookRef.current?.pageFlip();
        if (!pf || typeof pf.flip !== "function") return false;
        try { pf.flip(Math.max(0, target)); } catch { return false; }
        return true;
      },
      /**
       * Read the section id of the currently visible page. Used by
       * the host page to highlight the active section pill in the
       * top nav so the user always knows which prayer they're in.
       * Returns `null` on the trailing "__blank__" decorative page.
       */
      currentSectionId: () => {
        const pf = bookRef.current?.pageFlip();
        if (!pf) return null;
        let renderedIdx;
        try { renderedIdx = pf.getCurrentPageIndex(); }
        catch { return null; }
        const semantic = renderedPages.length - 1 - renderedIdx;
        const page = pages[semantic];
        if (!page || page.sectionId === "__blank__") return null;
        return page.sectionId;
      },
    };
    if (typeof ref === "function") ref(api);
    else ref.current = api;
  }, [ref, pages, renderedPages, dims.portrait]);

  if (pages.length === 0) {
    return (
      <div className="text-center py-10 text-muted-foreground" data-testid="siddur-book-empty">
        {t("siddur.empty_content")}
      </div>
    );
  }

  // Total page-count for the folio in the footer.
  return (
    <div
      ref={stageRef}
      className="siddur-book-stage flex items-center justify-center w-full"
      data-testid="siddur-book-stage"
    >
      <div className="siddur-book-spread">
        <HTMLFlipBook
          ref={bookRef}
          width={dims.width}
          height={dims.height}
          size="fixed"
          minWidth={240}
          maxWidth={680}
          minHeight={340}
          maxHeight={920}
          maxShadowOpacity={0.45}
          showCover={false}
          // Enable react-pageflip's built-in scroll-to-flip helper so
          // long prayer pages don't trap the touch gesture on phones.
          // Without this, a downward swipe on a long Amidah page just
          // sits there because the library captures the touchmove
          // before the browser can scroll the inner overflow.
          mobileScrollSupport={true}
          drawShadow={true}
          flippingTime={650}
          usePortrait={dims.portrait}
          startPage={startPage}
          className="tanakh-book siddur-book"
          autoSize={false}
          clickEventForward={true}
          useMouseEvents={true}
          swipeDistance={90}
          showPageCorners={true}
          disableFlipByClick={true}
          onFlip={(e) => onPageFlip?.(e.data)}
        >
          {renderedPages.map((p, i) => {
            const semanticIndex = renderedPages.length - 1 - i;
            return (
              <SiddurPage
                key={`siddur-pg-${semanticIndex}`}
                pageIndex={semanticIndex}
                totalPages={pages.length}
                side={i % 2 === 0 ? "left" : "right"}
                titleHe={titleHe}
                titleEn={titleEn}
                sectionTitleHe={p.sectionTitleHe}
                sectionTitleEn={p.sectionTitleEn}
                isFirstOfSection={p.isFirstOfSection}
                skipTachanunBanner={p.skipTachanunBanner}
                subsections={p.subsections || []}
                showInstructions={showInstructions}
              />
            );
          })}
        </HTMLFlipBook>
      </div>
    </div>
  );
});

export default SiddurBookReader;


// --------------------------------------------------------------------- //
// Pagination — section/subsection → page list
// --------------------------------------------------------------------- //

/**
 * Build the list of siddur pages.
 *
 * Each output page is `{ sectionId, sectionTitleHe, sectionTitleEn,
 * isFirstOfSection, skipTachanunBanner, subsections: [{ id, title_he,
 * title_en, paragraphs, dimmed }] }`. We always start a new page
 * when entering a new section so the section title is the first
 * thing the user sees.
 *
 * Char budgeting: each paragraph contributes its raw HTML length
 * (good-enough proxy for visual height since the font / line-height
 * is constant inside a page). Subsection title adds 50 chars. The
 * section title adds 100 chars on the page that opens it.
 */
function buildSiddurPages({ sections, tStatus, maxCharsPerPage = 1000 }) {
  const out = [];
  if (!sections || sections.length === 0) return out;

  const SECTION_TITLE_COST = 110;
  const SUBSECTION_TITLE_COST = 55;

  const skipTachanun = !!tStatus?.skip_tachanun;
  const skipReason = tStatus?.reason_he;

  for (const section of sections) {
    // The first page of a section always carries a section title.
    let currentSubs = [];
    let currentChars = SECTION_TITLE_COST;
    let bannerOnThisPage = null;

    // Will Tachanun banner sit at top of this section's first page?
    const sectionHasTachanun = (section.subsections || []).some(
      (ss) => (ss.tags || []).includes("tachanun"),
    );
    if (sectionHasTachanun && skipTachanun) {
      bannerOnThisPage = {
        reason_he: skipReason,
      };
      currentChars += 100; // banner visual cost
    }

    const flushPage = () => {
      if (currentSubs.length === 0 && !bannerOnThisPage) return;
      out.push({
        sectionId: section.id,
        sectionTitleHe: section.title_he,
        sectionTitleEn: section.title_en,
        isFirstOfSection: bannerOnThisPage !== null || out.length === 0 || out[out.length - 1]?.sectionId !== section.id,
        skipTachanunBanner: bannerOnThisPage,
        subsections: currentSubs,
      });
      currentSubs = [];
      currentChars = 0;
      bannerOnThisPage = null;
    };

    for (const ss of section.subsections || []) {
      const dimmed = (ss.tags || []).includes("tachanun") && skipTachanun;
      // Compute combined char weight of this subsection.
      const ssChars = (ss.paragraphs || []).reduce(
        (acc, p) => acc + (typeof p === "string" ? p.length : 0),
        0,
      ) + SUBSECTION_TITLE_COST;

      // CASE 1: subsection fits in remainder of current page.
      if (currentChars + ssChars <= maxCharsPerPage) {
        currentSubs.push({ ...ss, dimmed });
        currentChars += ssChars;
        continue;
      }

      // CASE 2: doesn't fit AND current page has content → flush, start new.
      if (currentSubs.length > 0) {
        flushPage();
      }

      // CASE 3: subsection itself is bigger than a whole page. We
      // must split its paragraphs across multiple pages. Title stays
      // with the FIRST chunk only.
      if (ssChars > maxCharsPerPage) {
        let firstChunk = true;
        let chunkParas = [];
        let chunkChars = SUBSECTION_TITLE_COST;
        for (const para of ss.paragraphs || []) {
          const pChars = (typeof para === "string" ? para.length : 0);
          if (chunkChars + pChars > maxCharsPerPage && chunkParas.length > 0) {
            currentSubs.push({
              ...ss,
              dimmed,
              title_he: firstChunk ? ss.title_he : "", // suppress title on continuation
              title_en: firstChunk ? ss.title_en : "",
              paragraphs: chunkParas,
              _continued: !firstChunk,
            });
            flushPage();
            firstChunk = false;
            chunkParas = [];
            chunkChars = 0;
          }
          chunkParas.push(para);
          chunkChars += pChars;
        }
        if (chunkParas.length > 0) {
          currentSubs = [{
            ...ss,
            dimmed,
            title_he: firstChunk ? ss.title_he : "",
            title_en: firstChunk ? ss.title_en : "",
            paragraphs: chunkParas,
            _continued: !firstChunk,
          }];
          currentChars = chunkChars;
        }
      } else {
        // Subsection fits within a page, just not on the current one.
        currentSubs = [{ ...ss, dimmed }];
        currentChars = ssChars;
      }
    }

    flushPage();
  }

  // The react-pageflip library always renders spreads (2 pages); if
  // we have an odd count, append a decorative blank so the last
  // prayer doesn't end up paired with an empty void.
  if (out.length % 2 !== 0) {
    out.push({
      sectionId: "__blank__",
      sectionTitleHe: "",
      sectionTitleEn: "",
      isFirstOfSection: false,
      skipTachanunBanner: null,
      subsections: [],
    });
  }
  return out;
}


// --------------------------------------------------------------------- //
// Dimensions — copied from BookReader3D so the spread looks identical
// --------------------------------------------------------------------- //

function computeDims(containerWidth, viewportHeight) {
  const w = typeof containerWidth === "number"
    ? containerWidth
    : typeof window !== "undefined" ? window.innerWidth : 1200;
  const h = typeof viewportHeight === "number"
    ? viewportHeight
    : typeof window !== "undefined" ? window.innerHeight : 900;

  let safeTop = 0;
  let safeBottom = 0;
  if (typeof window !== "undefined") {
    const cs = getComputedStyle(document.documentElement);
    safeTop = parseFloat(cs.getPropertyValue("--sat") || "0") || 0;
    safeBottom = parseFloat(cs.getPropertyValue("--sab") || "0") || 0;
  }

  const isMobile = w < 640;
  const isTablet = w >= 640 && w < 1024;
  const isPhoneLandscape = !isMobile && h < 500;
  // Chrome reserve: navbar (64) + page-nav bar (72 on phone, 64 on
  // desktop) + safe-areas. Phone tightens the toolbar.
  let chromeY;
  if (isMobile) chromeY = 44 + 36 + 72 + safeTop + safeBottom + 16;
  else if (isPhoneLandscape) chromeY = 48 + 56 + 24 + safeTop + safeBottom + 16;
  else chromeY = 64 + 64 + safeTop + safeBottom + 16;

  if (isMobile) {
    const pageW = Math.min(w - 16, 440);
    const idealH = Math.round(pageW * 1.38);
    const pageH = Math.max(360, Math.min(h - chromeY, idealH));
    return { width: pageW, height: pageH, portrait: true };
  }
  if (isPhoneLandscape) {
    const availW = w - 16;
    const pageW = Math.floor(availW / 2);
    const pageH = 480;
    return { width: pageW, height: pageH, portrait: false };
  }
  if (isTablet) {
    if (h > w) {
      const pageW = Math.min(w - 64, 560);
      const pageH = Math.min(h - chromeY, Math.round(pageW * 1.4));
      return { width: pageW, height: pageH, portrait: true };
    }
    const pageW = Math.min(Math.floor((w * 0.88 - 48) / 2), 560);
    const pageH = Math.min(h - chromeY, Math.round(pageW * 1.34));
    return { width: pageW, height: pageH, portrait: false };
  }
  // Desktop spread.
  const pageW = Math.min(Math.floor((w * 0.92 - 64) / 2), 680);
  const pageH = Math.min(h - chromeY, Math.round(pageW * 1.36));
  return { width: pageW, height: pageH, portrait: false };
}
